import { useState, useEffect } from 'react'
import { Save, AlertTriangle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { usePlans, refreshPlans } from '../../hooks/useBilling'
import { Card, naira } from '../ui'

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-teal-500 tabular-nums'

// One editable row per tier. Prices/limits write straight to the plans table
// (superadmin RLS) — no deploy needed. maxBranches is enforced by the DB
// trigger; maxUsers by the create-user function.
function PlanRow({ plan, showToast, onAudit }) {
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setForm({
      amount: String(plan.amount),
      maxBranches: plan.maxBranches == null ? '' : String(plan.maxBranches),
      maxUsers: plan.maxUsers == null ? '' : String(plan.maxUsers),
      planCode: plan.plan_code || '',
      description: plan.description || '',
    })
  }, [plan.tier, plan.amount, plan.maxBranches, plan.maxUsers, plan.plan_code, plan.description])

  if (!form) return null
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))
  const toLimit = (v) => (v.trim() === '' ? null : Math.max(1, parseInt(v, 10) || 1))

  async function save() {
    const amount = Number(form.amount)
    if (!Number.isFinite(amount) || amount < 0) return showToast('Amount must be a number ≥ 0.', 'error')
    setSaving(true)
    const { error } = await supabase.from('plans').update({
      amount,
      max_branches: toLimit(form.maxBranches),
      max_users: toLimit(form.maxUsers),
      paystack_plan_code: form.planCode.trim(),
      description: form.description.trim(),
      updated_at: new Date().toISOString(),
    }).eq('tier', plan.tier)
    setSaving(false)
    if (error) return showToast('Could not save: ' + error.message, 'error')
    await refreshPlans()
    onAudit?.('plan_catalogue_update', plan.name, {
      amount, max_branches: toLimit(form.maxBranches), max_users: toLimit(form.maxUsers),
    })
    showToast(`${plan.name} plan updated — now ${naira(amount)}/mo.`)
  }

  return (
    <div className="px-4 py-4 border-b border-gray-100 last:border-0">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-gray-800">{plan.name}</p>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-50 transition">
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Monthly (₦)</label>
          <input className={inputCls} type="number" min="0" value={form.amount} onChange={set('amount')} />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Max branches</label>
          <input className={inputCls} type="number" min="1" placeholder="∞" value={form.maxBranches} onChange={set('maxBranches')} />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Max users</label>
          <input className={inputCls} type="number" min="1" placeholder="∞" value={form.maxUsers} onChange={set('maxUsers')} />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Paystack plan code</label>
          <input className={inputCls + ' font-mono text-xs'} value={form.planCode} onChange={set('planCode')} />
        </div>
        <div className="col-span-2 sm:col-span-4">
          <label className="block text-[11px] font-semibold text-gray-500 mb-1">Description (shown on pricing cards)</label>
          <input className={inputCls} value={form.description} onChange={set('description')} />
        </div>
      </div>
    </div>
  )
}

export default function SettingsView({ showToast, onAudit }) {
  const plans = usePlans()

  return (
    <div className="space-y-3">
      <Card title="Plan catalogue" hint="changes apply immediately — no deploy">
        {plans.map((p) => <PlanRow key={p.tier} plan={p} showToast={showToast} onAudit={onAudit} />)}
      </Card>

      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          The <span className="font-semibold">monthly amount</span> here drives pricing displays and MRR
          reporting; the <span className="font-semibold">actual charge</span> comes from the Paystack plan
          behind the plan code. When changing a price, update the plan on the Paystack dashboard too (or
          create a new plan there and paste its code here). Branch limits are enforced when branches are
          added; lowering a limit doesn't remove existing branches.
        </p>
      </div>

      <p className="text-[11px] text-gray-400 px-1">
        Requires <span className="font-mono">plans_migration.sql</span> — until it runs, values shown are
        the built-in defaults and saving will fail.
      </p>
    </div>
  )
}
